import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';

const SORT_OPTIONS = [
    { key: null, direction: null, label: 'Default' },
    { key: 'price', direction: 'asc', label: 'Price (Lowest - Highest)' },
    { key: 'price', direction: 'desc', label: 'Price (Highest - Lowest)' },
    { key: 'gross_yield', direction: 'desc', label: 'Yield (Highest - Lowest)' },
    { key: 'gross_yield', direction: 'asc', label: 'Yield (Lowest - Highest)' },
    { key: 'created_at', direction: 'desc', label: 'Date (Newest - Oldest)' },
    { key: 'created_at', direction: 'asc', label: 'Date (Oldest - Newest)' },
    { key: 'beds', direction: 'desc', label: 'Beds (Most - Least)' },
    { key: 'beds', direction: 'asc', label: 'Beds (Least - Most)' },
];

const MobileSortDropdown = ({ sortConfig, onSortChange, disabled = false, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelect = (option) => {
        onSortChange(option.key, option.direction);
        setIsOpen(false);
    };

    const selectedOption = SORT_OPTIONS.find(
        opt => opt.key === sortConfig?.key && opt.direction === sortConfig?.direction
    ) || SORT_OPTIONS[0];

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`flex items-center gap-2 px-3 sm:px-4 h-10 sm:h-11 bg-white rounded-lg border border-gray-200 text-xs sm:text-sm font-medium text-[#36423F] hover:bg-gray-50 transition-colors min-w-[140px] sm:min-w-[200px] justify-between ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className="flex items-center gap-2 truncate">
                    <ArrowUpDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{selectedOption.label}</span>
                </span>
                {isOpen ? (
                    <ChevronUp size={16} className="text-gray-400 flex-shrink-0" />
                ) : (
                    <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                )}
            </button>

            {isOpen && (
                <div 
                    className="absolute top-full mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden max-h-[300px] overflow-y-auto"
                    role="listbox"
                >
                    {SORT_OPTIONS.map((option, index) => {
                        const isSelected = option.key === sortConfig?.key && option.direction === sortConfig?.direction;
                        return (
                            <button
                                key={`${option.key}-${option.direction}-${index}`}
                                onClick={() => handleSelect(option)}
                                className={`w-full px-4 py-3 text-left text-xs sm:text-sm font-medium transition-colors ${
                                    isSelected
                                        ? 'bg-[#55C5D0] text-white'
                                        : 'text-[#36423F] hover:bg-gray-50 active:bg-gray-100'
                                }`}
                                role="option"
                                aria-selected={isSelected}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MobileSortDropdown;
